import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from '@bluevalet/common-ui';
import IntlTelInput from 'intl-tel-input/react/build/IntlTelInput.cjs';

import { withOrder } from '../withOrder';
import { updateInformation } from '../../actions/information';
import { getInformation, InformationState } from '../../reducers/information';
import { getTravelType } from '../../reducers/cart';
import { getBooking, BookingState } from '../../reducers/booking';
import { TravelTypes } from '../../types';
import { isErrorField } from '../../utils';
import TravelInfo from '../TravelInfo';
import TravelType from '../TravelType';
import SiteInfo from '../SiteInfo';
import { NextStepButton } from '../NextStepButton';
import { getEndTrip } from '../../reducers/trip';
import { getSites } from '../../reducers/sites';
import { Trip, TripStatus } from '../../model';
import { Country } from '../../../../core/lang/model';
import { Site } from '../../../common/model';
import { State } from '../../reducers';

const isTripValid = (endTrip: Trip | undefined, booking: BookingState, arrivalSiteType: Site.Kind): boolean => {
  if (!endTrip) return false;
  const isAirportArrival = arrivalSiteType === Site.Kind.Airport;

  return (
    endTrip.status === TripStatus.Forced ||
    endTrip.status === TripStatus.Unknown ||
    endTrip.status === TripStatus.Listed ||
    (endTrip.status === TripStatus.Found && (isAirportArrival || booking.isArrivalSlotAvailable))
  );
};

interface OwnProps {
  forceValidation: boolean;
}

const InformationStep: FC<OwnProps> = ({ forceValidation: initialForceValidation }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const information = useSelector(getInformation) as InformationState | undefined;
  const travelType = useSelector(getTravelType) as string | undefined;
  const booking = useSelector(getBooking) as BookingState;
  const endTrip = useSelector((state: State) => getEndTrip(state));
  const arrivalSiteType = useSelector((state: State) => {
    const sites = getSites(state);
    const arrivalSite = sites.find(s => s.id.toString() === booking?.arrival?.site);
    return arrivalSite?.type ?? Site.Kind.Airport;
  });

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [forceValidation, setForceValidation] = useState(initialForceValidation);

  const onChange = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateInformation({
        ...information,
        [field]: event?.target?.value,
      }),
    );
  };

  const onChangeAddress = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateInformation({
        ...information,
        address: {
          ...information?.address,
          [field]: event?.target?.value,
        },
      }),
    );
  };

  const onChangeVehicle = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateInformation({
        ...information,
        vehicle: {
          ...information?.vehicle,
          [field]: event?.target?.value,
        },
      }),
    );
  };

  const onChangePhone = (phoneNumber: string) => {
    dispatch(updateInformation({ ...information, phoneNumber }));
  };

  const scrollToFirstError = () => {
    const scrollTo = (selector: string) => {
      const elem = document.querySelector(selector);
      if (elem) elem.scrollIntoView({ behavior: 'smooth' });
    };
    if (!travelType) {
      scrollTo('#FieldsetTravelType');
    } else if (!information?.lastName || !information?.firstName || !isPhoneValid) {
      scrollTo('#FieldsetCustomer');
    } else if (!information?.vehicle?.kind || !information?.vehicle?.color) {
      scrollTo('#FieldsetVehicle');
    } else if (!booking?.departure?.checkpoint || !booking?.arrival?.checkpoint) {
      scrollTo('#FieldsetTrip');
    } else if (!booking?.arrival?.tripNumber || !isTripValid(endTrip, booking, arrivalSiteType)) {
      scrollTo('#FieldsetTrip .Trip');
    }
  };

  const onDisabledSubmit = () => {
    setForceValidation(true);
    scrollToFirstError();
  };

  const isError = (field: string | undefined) => isErrorField(forceValidation, field);
  const getErrorMessage = (field: string | undefined) => (isError(field) ? t('error-required') : undefined);

  // ⚠ AWFUL STUFF HERE
  // because the onChangeValidity is not triggered with an initialValue,
  // but we need the input & utils file loaded for that...
  // Might be fixed in a later release of intl-tel-input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const intlInstance = window.intlTelInputGlobals?.instances[0];
      if (intlInstance) {
        intlInstance.promise.then(() => {
          setIsPhoneValid(intlInstance.isValidNumber());
        });
      }
    }, 600);
    return () => clearTimeout(timeoutId);
  }, []);

  const phoneNumber = information?.phoneNumber;

  useEffect(() => {
    const intlInstance = window.intlTelInputGlobals?.instances[0];

    if (intlInstance) {
      intlInstance.promise.then(() => {
        intlInstance.setNumber(phoneNumber ?? '');
        setIsPhoneValid(intlInstance.isValidNumber());
      });
    }
  }, [phoneNumber]);

  useEffect(() => {
    setIsFormValid(
      Boolean(
        information?.lastName &&
          information?.firstName &&
          information?.vehicle?.kind &&
          information?.vehicle?.color &&
          travelType &&
          isPhoneValid &&
          booking?.departure?.checkpoint &&
          booking?.arrival?.checkpoint &&
          booking?.arrival?.tripNumber &&
          isTripValid(endTrip, booking, arrivalSiteType),
      ),
    );
  }, [information, travelType, booking, isPhoneValid, endTrip, arrivalSiteType]);

  return (
    <div id="order-informations" className="bookingInformation bookingLogin bookingStep active">
      <h2>4. {t('booking-detail')}</h2>
      <div className="booking-content">
        <TravelType forceValidation={forceValidation} />

        <fieldset id="FieldsetCustomer">
          <legend>{t('booking-info')}</legend>
          <div className="form">
            <div className="field">
              <Input
                data-cy="lastName"
                id="lastName"
                name="lastName"
                label={`${t('lastName')} *`}
                type="text"
                placeholder={t('lastName-placeholder')}
                value={information?.lastName}
                onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChange('lastName', value)}
                error={isError(information?.lastName)}
                message={getErrorMessage(information?.lastName)}
              />
            </div>
            <div className="field">
              <Input
                data-cy="firstName"
                id="firstName"
                name="firstName"
                label={`${t('firstName')} *`}
                type="text"
                placeholder={t('firstName-placeholder')}
                value={information?.firstName}
                onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChange('firstName', value)}
                error={isError(information?.firstName)}
                message={getErrorMessage(information?.firstName)}
              />
            </div>
            <div className="field">
              <div className="label">
                <span className="input-label" title="Nom">
                  {`${t('phoneNumber')} *`}
                </span>
              </div>
              <IntlTelInput
                initialValue={information?.phoneNumber}
                onChangeNumber={onChangePhone}
                onChangeValidity={setIsPhoneValid}
                initOptions={{
                  initialCountry: i18n.language,
                  preferredCountries: Object.values(Country),
                  utilsScript: '/assets/static/js/intl-tel-input/build/js/utils.js',
                  separateDialCode: true,
                  showSelectedDialCode: true,
                  countrySearch: false,
                  autoPlaceholder: 'aggressive',
                }}
              />
              <input type="hidden" name="phoneNumber" value={information?.phoneNumber ?? ''} />
              <input type="hidden" name="phoneNumber-valid" value={String(isPhoneValid)} />
              <input type="hidden" name="email" value={information?.email ?? ''} />
            </div>
            {travelType === TravelTypes.pro && (
              <div className="field">
                <Input
                  data-cy="company"
                  id="company"
                  name="company"
                  label={t('company')}
                  type="text"
                  placeholder={t('company-placeholder')}
                  value={information?.company}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChange('company', value)}
                />
              </div>
            )}
          </div>
        </fieldset>

        {travelType === TravelTypes.pro && (
          <fieldset id="FieldsetAddress">
            <legend>{t('booking-address')}</legend>
            <div className="form">
              <div className="field">
                <Input
                  data-cy="addressFirstLine"
                  id="addressFirstLine"
                  name="address.firstLine"
                  label={`${t('addressFirstLine')} *`}
                  type="text"
                  placeholder={t('addressFirstLine-placeholder')}
                  value={information?.address?.firstLine}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeAddress('firstLine', value)}
                  error={isError(information?.address?.firstLine)}
                  message={getErrorMessage(information?.address?.firstLine)}
                />
              </div>
              <div className="field">
                <Input
                  data-cy="addressSecondLine"
                  id="addressSecondLine"
                  name="address.secondLine"
                  label={t('addressSecondLine')}
                  type="text"
                  placeholder={t('addressSecondLine-placeholder')}
                  value={information?.address?.secondLine}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeAddress('secondLine', value)}
                />
              </div>
              <div className="field">
                <Input
                  data-cy="zipCode"
                  id="zipCode"
                  name="address.zipCode"
                  label={`${t('addressZipCode')} *`}
                  type="text"
                  placeholder={t('addressZipCode-placeholder')}
                  value={information?.address?.zipCode}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeAddress('zipCode', value)}
                  error={isError(information?.address?.zipCode)}
                  message={getErrorMessage(information?.address?.zipCode)}
                />
              </div>
              <div className="field">
                <Input
                  data-cy="city"
                  id="city"
                  name="address.city"
                  label={`${t('addressCity')} *`}
                  type="text"
                  placeholder={t('addressCity-placeholder')}
                  value={information?.address?.city}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeAddress('city', value)}
                  error={isError(information?.address?.city)}
                  message={getErrorMessage(information?.address?.city)}
                />
              </div>
              <div className="field">
                <Input
                  data-cy="country"
                  id="country"
                  name="address.country"
                  label={`${t('addressCountry')} *`}
                  type="text"
                  placeholder={t('addressCountry-placeholder')}
                  value={information?.address?.country}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeAddress('country', value)}
                  error={isError(information?.address?.country)}
                  message={getErrorMessage(information?.address?.country)}
                />
              </div>
            </div>
          </fieldset>
        )}

        <fieldset id="FieldsetVehicle">
          <legend>{t('booking-vehicle-legend')}</legend>
          <div className="form">
            <div className="field">
              <Input
                data-cy="vehicle.kind"
                id="vehicle.kind"
                name="vehicle.kind"
                label={`${t('vehicleKind')} *`}
                type="text"
                placeholder={t('vehicleKind-placeholder')}
                value={information?.vehicle?.kind || ''}
                onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeVehicle('kind', value)}
                error={isError(information?.vehicle?.kind)}
                message={getErrorMessage(information?.vehicle?.kind)}
              />
            </div>
            <div className="field">
              <Input
                data-cy="vehicle.color"
                id="vehicleColor"
                name="vehicle.color"
                label={`${t('vehicleColor')} *`}
                type="text"
                placeholder={t('vehicleColor-placeholder')}
                value={information?.vehicle?.color || ''}
                onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeVehicle('color', value)}
                error={isError(information?.vehicle?.color)}
                message={getErrorMessage(information?.vehicle?.color)}
              />
            </div>
          </div>
          <SiteInfo />
        </fieldset>

        <fieldset id="FieldsetTrip">
          <legend>{t('booking-trip-legend')}</legend>
          <div className="form travelInfoForm">
            {travelType === TravelTypes.bvb && (
              <div className="field">
                <Input
                  data-cy="expensesTitle"
                  id="expensesTitle"
                  name="expensesTitle"
                  label={t('expensesTitle')}
                  type="text"
                  placeholder={t('expensesTitle-placeholder')}
                  value={information?.expensesTitle}
                  onChange={(value: React.ChangeEvent<HTMLInputElement>) => onChangeVehicle('expensesTitle', value)}
                />
              </div>
            )}
            <TravelInfo forceValidation={forceValidation} />
          </div>
        </fieldset>
      </div>

      <NextStepButton label={t('booking-go-to-payment')} disabled={!isFormValid} onDisabledClick={onDisabledSubmit} />
    </div>
  );
};

export default withOrder(InformationStep);
